// components/features/annonces/AnnonceRow.tsx

import { Annonce } from "../../../types/features/Annonce";
import TableRow from "../../ui/TableRow";
import { 
  Info, AlertTriangle, CheckCircle, AlertCircle,
  Eye, Send, Clock
} from "lucide-react";

interface AnnonceRowProps {
  annonce: Annonce;
  onAction: (annonce: Annonce) => void;
  onSelect?: (annonce: Annonce, isSelected: boolean) => void;
  isSelected?: boolean;
  selectable?: boolean;
}

const getTypeIcon = (type: string) => {
  const icons = {
    info: Info,
    warning: AlertTriangle,
    success: CheckCircle,
    danger: AlertCircle,
  };
  return icons[type as keyof typeof icons] || Info;
};

const getTypeColor = (type: string) => {
  const colors = {
    info: "bg-blue-100 text-blue-700 border-blue-200",
    warning: "bg-amber-100 text-amber-700 border-amber-200",
    success: "bg-green-100 text-green-700 border-green-200",
    danger: "bg-red-100 text-red-700 border-red-200",
  };
  return colors[type as keyof typeof colors] || "bg-gray-100 text-gray-700";
};

const getTypeLabel = (type: string) => {
  const labels = {
    info: "Information",
    warning: "Avertissement",
    success: "Succès",
    danger: "Urgent",
  };
  return labels[type as keyof typeof labels] || type;
};

export default function AnnonceRow({
  annonce,
  onAction,
  onSelect,
  isSelected = false,
  selectable = false
}: AnnonceRowProps) {
  const Icon = getTypeIcon(annonce.type);
  const isPublished = annonce.is_published;

  return (
    <TableRow
      item={annonce}
      onAction={onAction}
      onSelect={onSelect}
      isSelected={isSelected}
      selectable={selectable}
      actionable={true}
    >
      {/* Colonne Titre */}
      <td className="py-3 px-4">
        <div className="flex items-center space-x-3">
          <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 border ${getTypeColor(annonce.type)}`}>
            <Icon size={18} />
          </div>
          <div className="min-w-0 flex-1">
            <span className="text-sm font-medium text-gray-900 block truncate max-w-[200px]">
              {annonce.title}
            </span>
            <span className="text-xs text-gray-500 block truncate max-w-[300px]">
              {annonce.content.length > 100 ? annonce.content.substring(0, 100) + "..." : annonce.content}
            </span>
          </div>
        </div>
      </td>

      {/* Colonne Type */}
      <td className="hidden sm:table-cell py-3 px-4 whitespace-nowrap">
        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${getTypeColor(annonce.type)}`}>
          {getTypeLabel(annonce.type)}
        </span>
      </td>

      {/* Colonne Destinataires */}
      <td className="hidden md:table-cell py-3 px-4 whitespace-nowrap">
        {annonce.send_to_all ? (
          <span className="inline-flex items-center gap-1 text-xs text-gray-500">
            <CheckCircle size={12} className="text-green-500" />
            Tous
          </span>
        ) : (
          <span className="text-xs text-gray-500">
            {annonce.recipients?.length || 0} sélectionné(s)
          </span>
        )}
      </td>

      {/* Colonne Statut */}
      <td className="hidden lg:table-cell py-3 px-4 whitespace-nowrap">
        {isPublished ? (
          <div className="flex items-center gap-1">
            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
              Publiée
            </span>
            {annonce.sent_count > 0 && (
              <span className="text-xs text-gray-400">
                ({annonce.sent_count} envoyés)
              </span>
            )}
          </div>
        ) : (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-500">
            Brouillon
          </span>
        )}
      </td>

      {/* Colonne Date */}
      <td className="hidden xl:table-cell py-3 px-4 whitespace-nowrap">
        <div className="flex flex-col">
          <span className="text-xs text-gray-500">
            {annonce.published_at
              ? new Date(annonce.published_at).toLocaleDateString("fr-FR", {
                  day: "2-digit",
                  month: "2-digit",
                  year: "numeric"
                })
              : "—"}
          </span>
          {annonce.published_at && (
            <span className="text-[10px] text-gray-400">
              {new Date(annonce.published_at).toLocaleTimeString("fr-FR", {
                hour: "2-digit",
                minute: "2-digit"
              })}
            </span>
          )}
        </div>
      </td>
    </TableRow>
  );
}